/* eslint-disable @typescript-eslint/naming-convention */
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { QueryRef } from 'apollo-angular';
import { Subscription } from 'rxjs';
import { SearchResultsGQL, SearchResultsQuery, SearchResultsQueryVariables } from 'src/generated/graphql.generated';
import { SubSink } from 'subsink';

@Component({
  selector: 'app-search-results',
  templateUrl: './search-results.component.html',
  styleUrls: ['./search-results.component.scss'],
})
export class SearchResultsComponent implements OnInit, OnDestroy {
  subs = new SubSink();
  query = '';
  results: SearchResultsQuery['searchJobs']['results'] = [];
  total = 0;
  loading = false;
  failed = false;
  pageSize = 20;
  currentOffset = 0;

  private searchWatchQuery: QueryRef<SearchResultsQuery, SearchResultsQueryVariables> | null = null;
  private watchQuerySub: Subscription | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private searchResultsGQL: SearchResultsGQL,
  ) {}

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      this.query = params['q'] || '';
      if (this.query) {
        this.currentOffset = 0;
        this.search(true);
      } else {
        this.results = [];
        this.total = 0;
        this.loading = false;
        this.failed = false;
      }
    });
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.resetWatchQuery();
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
  }

  private resetWatchQuery() {
    if (this.searchWatchQuery) {
      this.searchWatchQuery.stopPolling();
      this.watchQuerySub?.unsubscribe();
      this.watchQuerySub = null;
      this.searchWatchQuery = null;
    }
  }

  search(fresh = false): void {
    if (!this.query) { return; }

    const variables: SearchResultsQueryVariables = {
      search: this.query,
      limit: this.pageSize,
      offset: this.currentOffset,
    };

    this.loading = true;
    this.failed = false;

    if (this.searchWatchQuery && !fresh) {
      this.searchWatchQuery.refetch(variables);
      return;
    }

    this.resetWatchQuery();

    this.searchWatchQuery = this.searchResultsGQL.watch(variables, {
      fetchPolicy: 'network-only',
    });

    this.watchQuerySub = this.searchWatchQuery.valueChanges.subscribe((res) => {
      this.loading = res.loading;
      if (res.loading) { return; }
      // Append on load-more (offset > 0); replace on fresh search.
      this.results = this.currentOffset === 0
        ? res.data.searchJobs.results
        : [...this.results, ...res.data.searchJobs.results];
      this.total = res.data.searchJobs.total;
    }, (err) => {
      console.error('Failed to load search results', err);
      this.resetWatchQuery();
      this.loading = false;
      if (this.currentOffset === 0) {
        this.results = [];
        this.total = 0;
      } else {
        this.currentOffset -= this.pageSize;
      }
      this.failed = true;
    });
    this.subs.sink = this.watchQuerySub;
  }

  loadMore(): void {
    this.currentOffset += this.pageSize;
    this.search();
  }

  openJob(result: SearchResultsQuery['searchJobs']['results'][0]): void {
    this.router.navigate(['/jobs', 'v3', result.id, 'overview'], {
      queryParams: { zone: result.zoneId },
    });
  }

  onSearchInput(): void {
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      if (this.query && this.route.snapshot.queryParamMap.get('q') === this.query) {
        this.currentOffset = 0;
        this.search(true);
        return;
      }

      this.router.navigate(['/search'], {
        queryParams: { q: this.query },
        queryParamsHandling: 'merge',
      });
    }, 300);
  }
}